import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  ArrowUpRight,
  Box,
  FileText,
  FolderKanban,
  Mail,
  PenSquare,
  Sparkles,
  Users,
} from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { getAdminProjects } from "../../../api/projectsAdmin";
import { getAdminBlogPosts } from "../../../api/blogAdmin";
import { getAdminSubscribers } from "../../../api/subscribeAdmin";
import { getAdminContactUsRequests } from "../../../api/contactUsAdmin";
import { getAdminCallRequests } from "../../../api/callRequestsAdmin";
import {
  DEFAULT_CONTENT_PAGE,
  DEFAULT_PRODUCT_TYPE,
  getDashboardNavItems,
} from "../dashboardNavConfig";

const toList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
};

const formatToday = () =>
  new Date().toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

const QUICK_ACTIONS = [
  {
    id: "content",
    to: `/dashboard/content/${DEFAULT_CONTENT_PAGE}`,
    label: "Edit content",
    description: "Update page copy, images and sections",
    icon: FileText,
    accent: "from-blue-500/15 to-blue-600/5 text-blue-700",
  },
  {
    id: "products",
    to: `/dashboard/products/${DEFAULT_PRODUCT_TYPE}`,
    label: "Manage products",
    description: "Boards, accessories and metal frames",
    icon: Box,
    accent: "from-orange-500/15 to-orange-600/5 text-orange-700",
  },
  {
    id: "projects",
    to: "/dashboard/projects",
    label: "Projects",
    description: "Portfolio entries and gallery images",
    icon: FolderKanban,
    accent: "from-violet-500/15 to-violet-600/5 text-violet-700",
  },
  {
    id: "blog",
    to: "/dashboard/blog",
    label: "Blog posts",
    description: "Publish and edit articles",
    icon: PenSquare,
    accent: "from-emerald-500/15 to-emerald-600/5 text-emerald-700",
  },
  {
    id: "contact-us",
    to: "/dashboard/contact-us",
    label: "Contact requests",
    description: "Review messages from visitors",
    icon: Mail,
    accent: "from-rose-500/15 to-rose-600/5 text-rose-700",
  },
  {
    id: "users",
    to: "/dashboard/users",
    label: "Users",
    description: "Manage dashboard accounts",
    icon: Users,
    accent: "from-slate-500/15 to-slate-600/5 text-slate-700",
  },
];

const StatCard = ({ label, value, loading, icon: Icon, tint }) => (
  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex flex-col gap-3">
    <div className="flex items-start justify-between gap-3">
      <div
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${tint}`}
      >
        <Icon size={18} strokeWidth={2} />
      </div>
      {loading ? (
        <div className="h-8 w-14 rounded-lg bg-slate-100 animate-pulse" />
      ) : (
        <p className="text-3xl font-bold text-slate-900 tabular-nums">{value}</p>
      )}
    </div>
    <p className="text-sm text-slate-600">{label}</p>
  </div>
);

const DashboardOverview = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [stats, setStats] = useState({
    projects: null,
    blogPosts: null,
    subscribers: null,
    contactRequests: null,
    callRequests: null,
  });
  const [loadingStats, setLoadingStats] = useState(true);

  const navItems = useMemo(() => getDashboardNavItems(t), [t, i18n.language]);
  const sectionCount = navItems.length - 1;

  const displayName = useMemo(() => {
    const name = user?.name || user?.username || user?.email;
    return typeof name === "string" && name.trim() ? name.trim() : "Admin";
  }, [user]);

  useEffect(() => {
    let isMounted = true;

    const loadStats = async () => {
      setLoadingStats(true);
      const results = await Promise.allSettled([
        getAdminProjects(),
        getAdminBlogPosts(),
        getAdminSubscribers(),
        getAdminContactUsRequests(),
        getAdminCallRequests(),
      ]);

      if (!isMounted) return;

      const [projects, blog, subscribers, contact, calls] = results;
      setStats({
        projects: projects.status === "fulfilled" ? toList(projects.value).length : 0,
        blogPosts: blog.status === "fulfilled" ? toList(blog.value).length : 0,
        subscribers: subscribers.status === "fulfilled" ? toList(subscribers.value).length : 0,
        contactRequests: contact.status === "fulfilled" ? toList(contact.value).length : 0,
        callRequests: calls.status === "fulfilled" ? toList(calls.value).length : 0,
      });
      setLoadingStats(false);
    };

    loadStats();
    return () => {
      isMounted = false;
    };
  }, []);

  const statCards = [
    { key: "projects", label: "Projects", value: stats.projects ?? 0, icon: FolderKanban, tint: "from-violet-100 to-violet-50 text-violet-600" },
    { key: "blog", label: "Blog posts", value: stats.blogPosts ?? 0, icon: PenSquare, tint: "from-emerald-100 to-emerald-50 text-emerald-600" },
    { key: "subscribers", label: "Subscribers", value: stats.subscribers ?? 0, icon: Mail, tint: "from-sky-100 to-sky-50 text-sky-600" },
    { key: "inbox", label: "Inbox items", value: (stats.contactRequests ?? 0) + (stats.callRequests ?? 0), icon: Users, tint: "from-amber-100 to-amber-50 text-amber-600" },
  ];

  return (
    <div className="space-y-6">
      <section className="relative overflow-hidden rounded-2xl border border-[#0b1f3f]/10 bg-gradient-to-br from-[#0b1f3f] via-[#122a4d] to-[#1a3a6b] p-6 sm:p-8 text-white shadow-lg">
        <div
          className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#FE7E3F]/20 blur-3xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-white/5 blur-2xl"
          aria-hidden
        />

        <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-medium uppercase tracking-wider text-white/80">
              <Sparkles size={14} className="text-[#FE7E3F]" />
              {t("dashboard.overview")}
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight">
              {getGreeting()}, {displayName}
            </h1>
            <p className="mt-3 text-sm sm:text-base text-white/75 leading-relaxed">
              {t("dashboard.subtitle")} You have access to {sectionCount} management sections — pick a
              quick action below or use the sidebar to dive in.
            </p>
          </div>

          <div className="shrink-0 rounded-xl border border-white/10 bg-white/10 px-4 py-3 backdrop-blur-sm">
            <p className="text-xs uppercase tracking-widest text-white/60">Today</p>
            <p className="mt-1 text-sm font-medium text-white">{formatToday()}</p>
          </div>
        </div>
      </section>

      <section aria-label="Dashboard statistics">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((card) => (
            <StatCard
              key={card.key}
              label={card.label}
              value={card.value}
              loading={loadingStats}
              icon={card.icon}
              tint={card.tint}
            />
          ))}
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-3">
        <section
          className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6"
          aria-labelledby="quick-actions-heading"
        >
          <div className="mb-5 flex items-center justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">Shortcuts</p>
              <h2 id="quick-actions-heading" className="text-xl font-bold text-slate-900">
                Quick actions
              </h2>
            </div>
            <Link
              to={`/dashboard/content/${DEFAULT_CONTENT_PAGE}`}
              className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-[#0b1f3f] hover:text-[#FE7E3F] transition-colors"
            >
              Get started
              <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {QUICK_ACTIONS.map((action) => {
              const Icon = action.icon;
              return (
                <Link
                  key={action.id}
                  to={action.to}
                  className="group flex items-start gap-4 rounded-xl border border-slate-200 p-4 transition-all hover:border-slate-300 hover:shadow-md hover:-translate-y-0.5"
                >
                  <div
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${action.accent}`}
                  >
                    <Icon size={20} strokeWidth={2} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-semibold text-slate-900 group-hover:text-[#0b1f3f]">
                        {action.label}
                      </h3>
                      <ArrowUpRight
                        size={16}
                        className="shrink-0 text-slate-400 opacity-0 transition-opacity group-hover:opacity-100"
                      />
                    </div>
                    <p className="mt-1 text-sm text-slate-500 leading-snug">{action.description}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6 flex flex-col">
          <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">Guide</p>
          <h2 className="text-xl font-bold text-slate-900 mb-4">Getting started</h2>

          <ol className="space-y-4 flex-1">
            {[
              "Use Content to update homepage, about, and footer text.",
              "Add or edit products under Boards, Accessories, or Metal Frames.",
              "Check Contact Us and Calls Requested for new visitor messages.",
              "Switch language (EN / AR / KU) before editing localized content.",
            ].map((tip, index) => (
              <li key={tip} className="flex gap-3 text-sm text-slate-600 leading-relaxed">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#0b1f3f] text-xs font-bold text-white">
                  {index + 1}
                </span>
                <span className="pt-0.5">{tip}</span>
              </li>
            ))}
          </ol>

          <div className="mt-6 rounded-xl bg-slate-50 border border-slate-200 p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Site preview</p>
            <p className="mt-1 text-sm text-slate-600">
              Open the public website in a new tab to verify your changes live.
            </p>
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-2 rounded-lg bg-[#0b1f3f] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#132d54]"
            >
              View website
              <ArrowUpRight size={16} />
            </a>
          </div>
        </section>
      </div>
    </div>
  );
};

export default DashboardOverview;
